import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { IconButton } from '@atlaskit/button/new';
import Form from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

import type { CustomLengthType, CustomToneType } from '../../../types/customizationTypes';
import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { AudioFeature } from '../../../types/audioFeatureType';
import { RestartBriefingButton } from '../sub-components/RestartBriefingButton';

import { DurationSelector } from './customize/DurationSelector';
import { ToneSelector } from './customize/ToneSelector';

const styles = cssMap({
	container: {
		padding: token('space.200'),
	},
	formWrapper: {
		paddingInline: token('space.075'),
	},
	buttonWrapper: {
		marginTop: token('space.150'),
	},
});

const i18n = defineMessages({
	customizeHeader: {
		id: 'audio.audio-popup.customize-header',
		defaultMessage: 'Customize',
		description: 'The header for the customization panel in the audio popup',
	},
	backButtonLabel: {
		id: 'audio.audio-popup.back-button-label',
		defaultMessage: 'Back',
		description: 'The label for the back button in the customization panel',
	},
});

interface CustomizationFormValues {
	length: CustomLengthType;
	tone: {
		value: CustomToneType;
	};
}

export type AudioBriefingCustomizePanelProps = {
	onBack: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
};

export const AudioBriefingCustomizePanel = ({
	onBack,
	audioRef,
}: AudioBriefingCustomizePanelProps) => {
	const { formatMessage } = useIntl();

	const [{ hasFetched, activeFeature, customization, lastGeneratedBriefingConfig }] =
		useAudioBriefing();

	const hasDiverged =
		customization.tone !== lastGeneratedBriefingConfig?.customization?.tone ||
		customization.length !== lastGeneratedBriefingConfig?.customization?.length;

	const shouldShowRestartButton =
		hasFetched && activeFeature === AudioFeature.BRIEFING && hasDiverged;

	return (
		<Box xcss={styles.container}>
			<Stack space="space.050">
				<Inline space="space.100" alignBlock="center">
					<IconButton
						icon={ChevronLeftIcon}
						onClick={onBack}
						appearance="subtle"
						label={formatMessage(i18n.backButtonLabel)}
						spacing="compact"
						testId="audio-briefing-customize-back-button"
					/>
					<Heading size="xsmall">{formatMessage(i18n.customizeHeader)}</Heading>
				</Inline>
				<Box xcss={styles.formWrapper}>
					<Form<CustomizationFormValues> onSubmit={() => {}}>
						{({ formProps }) => (
							<form {...formProps}>
								<Stack space="space.100">
									<ToneSelector />
									<DurationSelector />
									{shouldShowRestartButton && (
										<Box xcss={styles.buttonWrapper}>
											<RestartBriefingButton audioRef={audioRef} source="customizePanel" />
										</Box>
									)}
								</Stack>
							</form>
						)}
					</Form>
				</Box>
			</Stack>
		</Box>
	);
};
